let deeplabModelPromise = null

/** Pascal VOC: class index 0 is always semantic background. */
const PASCAL_BACKGROUND_LABEL = 0

/**
 * Minimum share of "foreground" pixels; below this, segmentation is treated as failed
 * and we fall back to the full frame (so outlines are not stripped to a thin band).
 */
const MIN_FOREGROUND_FRACTION = 0.02

async function loadDeeplabModel() {
  if (deeplabModelPromise) return deeplabModelPromise
  deeplabModelPromise = (async () => {
    const deeplab = await import('@tensorflow-models/deeplab')
    const model = await deeplab.load({ base: 'pascal', quantizationBytes: 2 })
    return model
  })()
  return deeplabModelPromise
}

/**
 * Returns a binary Uint8ClampedArray mask (1=object, 0=background) in the same resolution as `inputCanvas`.
 * Uses raw Pascal VOC class indices from DeepLab — not "most common RGB" — so close-up faces stay fully in the mask
 * (the old heuristic treated dominant "person" pixels as background and erased most of the face from the edges).
 *
 * @param {HTMLCanvasElement} inputCanvas
 * @returns {Promise<Uint8ClampedArray>}
 */
export async function getDeeplabObjectMaskBinary(inputCanvas) {
  const model = await loadDeeplabModel()
  const w = inputCanvas.width
  const h = inputCanvas.height
  const total = w * h

  const raw = model.predict(inputCanvas)
  try {
    const [segH, segW] = raw.shape
    const mapData = await raw.array()

    const mask = new Uint8ClampedArray(total)
    let foregroundPixels = 0

    for (let y = 0; y < h; y++) {
      const sy = Math.min(segH - 1, Math.max(0, Math.floor((y * segH) / h)))
      for (let x = 0; x < w; x++) {
        const sx = Math.min(segW - 1, Math.max(0, Math.floor((x * segW) / w)))
        const label = mapData[sy][sx]
        const isFg = label !== PASCAL_BACKGROUND_LABEL
        const v = isFg ? 1 : 0
        mask[y * w + x] = v
        foregroundPixels += v
      }
    }

    if (foregroundPixels < total * MIN_FOREGROUND_FRACTION) {
      mask.fill(1)
    }

    return mask
  } finally {
    raw.dispose()
  }
}
